/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef } from "react";
import Logo from "../assets/kwf-logo.png";
import "bootstrap/dist/css/bootstrap.min.css";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toggleAuth } from "../Redux/Slices/auth-slice";
import { scrollToTop } from "../constent/helper";
import bgColor from "../assets/programs-bg.webp";

function Header({ parent }) {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [scrollDirection, setScrollDirection] = useState("up"); // "up" or "down"
  const [isAtTop, setIsAtTop] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { isLogin, userInfo } = useSelector((state) => state.authSlice || {});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const mobileNavRef = useRef(null);

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const handleLogout = () => {
    dispatch(toggleAuth({ isLogin: false, userInfo: null }));
    navigate("/");
    localStorage.removeItem("activeTab");
    setIsNavOpen(false);
  };

  const handleLinkClick = () => {
    scrollToTop();
    setIsNavOpen(false);
  };

  // Close mobile nav when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isNavOpen &&
        mobileNavRef.current &&
        !mobileNavRef.current.contains(event.target)
      ) {
        setIsNavOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isNavOpen]);

  // Prevent body scroll when mobile nav is open
  useEffect(() => {
    if (isNavOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isNavOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Check if at top
      if (currentScrollY < 50) {
        setIsAtTop(true);
        setScrollDirection("up");
      } else {
        setIsAtTop(false);

        // Determine scroll direction
        if (currentScrollY > lastScrollY) {
          // Scrolling down
          setScrollDirection("down");
        } else {
          // Scrolling up
          setScrollDirection("up");
        }
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  // Determine navbar state
  //
  // Historically the "scroll down" branch intentionally omitted a bg so
  // the navbar could visually "hide". In practice this leaves the fixed
  // navbar transparent and ALL dashboard content scrolls visibly behind
  // it. For dashboard pages we always keep the navbar opaque, so page
  // h1s / section headers can't bleed through the sticky nav as the
  // user scrolls.
  const getNavbarClasses = () => {
    if (parent === "home") {
      if (isAtTop) {
        return "absolute top-0 left-0 right-0 z-50 bg-transparent";
      } else if (scrollDirection === "up") {
        return "fixed top-0 left-0 right-0 z-50 bg-cover";
      } else {
        return "fixed top-0 left-0 right-0 z-50";
      }
    }
    // Non-home pages (dashboard / admin / listing): ALWAYS keep an
    // opaque background. bg-white as a fallback covers the moment the
    // background image hasn't downloaded yet; the inline style still
    // layers the bg image on top when the scroll state wants it.
    return "fixed top-0 left-0 right-0 z-50 bg-white shadow-sm bg-cover";
  };

  const isActive = (to) => {
    if (!to) return false;
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  const getLinkStyles = (to) => {
    const activeClass = "text-primary-green hover:text-primary-green focus:text-primary-green";
    const inactiveClass = "text-white font-semibold hover:text-primary-green transition-colors";

    if (isActive(to)) return `${activeClass} font-semibold`;

    if (parent === "home") {
      if (isAtTop) {
        return inactiveClass;
      } else if (scrollDirection === "up") {
        return inactiveClass;
      }
    } else {
      if (isAtTop) {
        return inactiveClass;
      } else if (scrollDirection === "up") {
        return inactiveClass;
      }
    }
    return inactiveClass;
  };

  const getMobileLinkStyles = (to) => {
    const activeClass = "text-primary-green hover:text-primary-green focus:text-primary-green";
    const inactiveClass = "text-black font-semibold hover:text-gray-700 transition-colors";
    return isActive(to) ? `${activeClass} font-semibold` : inactiveClass;
  };

  const getLogoText=()=>{
if( parent === "home" && isAtTop){
  return 'text-black'
}else{
  return 'text-white'
}
  }

  const shouldShowBgImage =
    parent !== "home" && !isAtTop;
  const showBgImageHome =
    parent === "home" && !isAtTop;
  const showBgImageLogin = parent === "login" || parent === "family";

  return (
    <>
      {/* Overlay for mobile */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 lg:hidden z-[98] ${
          isNavOpen ? "opacity-50 visible" : "opacity-0 invisible"
        }`}
        onClick={() => setIsNavOpen(false)}
      ></div>

      <div
        className={`transition-all duration-300 ease-in-out w-full ${getNavbarClasses()}`}
        style={{
          backgroundImage:
            shouldShowBgImage || showBgImageHome || showBgImageLogin
              ? `url(${bgColor})`
              : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="">
          <div className="flex justify-between items-center max-w-[1380px] mx-auto lg:px-8 md:px-6 px-4 w-full">
            {/* Logo and Hamburger */}
            <div className={`flex justify-between items-center z-[99] ${isLogin ? 'lg:w-max w-full':'w-full'}`}>
              <div className="py-[4px]">
                <Link
                  to="/"
                  onClick={handleLinkClick}
                  style={{ padding: "0px" }}
                  className={`flex flex-col gap-0 items-center ${getLogoText()}`}
                >
                  <h2 className="text-[24px] font-medium leading-6 mb-[6px]">KWF</h2>
                  <h4 className="text-[12px] font-medium leading-3 whitespace-nowrap">Khadim Welfare Foundation</h4>
                </Link>
              </div>
              <button
                className={`lg:hidden shadow-none flex flex-col gap-1.5 p-2 w-max cursor-pointer bg-transparent ${
                  parent === "home" && isAtTop ? "text-white" : "text-white"
                }`}
                onClick={toggleNav}
                aria-label="Toggle menu"
              >
                <div
                  className={`w-6 h-0.5 bg-current transition-all duration-300 ${
                    isNavOpen ? "rotate-45 translate-y-2" : ""
                  }`}
                ></div>
                <div
                  className={`w-6 h-0.5 bg-current transition-all duration-300 ${
                    isNavOpen ? "opacity-0" : ""
                  }`}
                ></div>
                <div
                  className={`w-6 h-0.5 bg-current transition-all duration-300 ${
                    isNavOpen ? "-rotate-45 -translate-y-2" : ""
                  }`}
                ></div>
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className={`hidden lg:flex w-full items-center ml-8  ${isLogin ? 'justify-end':'justify-between'}`}>
              {!isLogin && (
                <div className="flex items-center gap-6">
                  <Link to="/" className={getLinkStyles("/")}>
                    Home
                  </Link>
                  <Link
                    to="/programs"
                    onClick={() => scrollToTop()}
                    className={getLinkStyles("/programs")}
                  >
                    Programs
                  </Link>
                  <Link
                    to="/aboutus"
                    onClick={() => scrollToTop()}
                    className={getLinkStyles("/aboutus")}
                  >
                    About us
                  </Link>
                  <Link
                    to="/casesfeeds"
                    onClick={() => scrollToTop()}
                    className={getLinkStyles("/casesfeeds")}
                  >
                    Projects
                  </Link>
                  <Link
                    to="/blogs"
                    onClick={() => scrollToTop()}
                    className={getLinkStyles("/blogs")}
                  >
                    Blogs
                  </Link>
                  {!isLogin && (
                    <Link
                      to="/login"
                      onClick={() => scrollToTop()}
                      className={getLinkStyles("/login")}
                    >
                      Login
                    </Link>
                  )}
                </div>
              )}
              <div className="flex items-center gap-6 justify-end">
                {isLogin ? (
                  userInfo?.user?.role === "admin" ? (
                    <>
                      <Link to="/dashboard" className={getLinkStyles("/dashboard")}>
                        Dashboard
                      </Link>
                      <Link to="/familylisting" className={getLinkStyles("/familylisting")}>
                        Family Listing
                      </Link>
                      <Link to="/memberlisting" className={getLinkStyles("/memberlisting")}>
                        Member Listing
                      </Link>
                      <Link
                        to="/propertydetailpage"
                        className={getLinkStyles("/propertydetailpage")}
                      >
                        Property Listing
                      </Link>
                      <a
                        onClick={handleLogout}
                        style={{ cursor: "pointer" }}
                        className={'primary-button'}
                      >
                        Logout
                      </a>
                    </>
                  ) : userInfo?.user?.role === "medicalstaff" ? (
                    <>
                      <Link
                        to="/dashboard/verify_fingerprint"
                        className={getLinkStyles("/dashboard/verify_fingerprint")}
                      >
                        Dashboard
                      </Link>
                      <a
                        onClick={handleLogout}
                        style={{ cursor: "pointer" }}
                        className={'primary-button'}
                      >
                        Logout
                      </a>
                    </>
                  ) : (
                    <>
                      <Link to="/familylisting" className={getLinkStyles("/familylisting")}>
                        Family Listing
                      </Link>
                      <Link to="/memberlisting" className={getLinkStyles("/memberlisting")}>
                        Member Listing
                      </Link>
                      <Link
                        to="/propertydetailpage"
                        className={getLinkStyles("/propertydetailpage")}
                      >
                        Property Listing
                      </Link>
                      <a
                        onClick={handleLogout}
                        style={{ cursor: "pointer" }}
                        className={'primary-button'}
                      >
                        Logout
                      </a>
                    </>
                  )
                ) : (
                  <Link
                    to="/donation"
                    onClick={() => scrollToTop()}
                    className="primary-button"
                  >
                    DONATE
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Sidebar */}
      <div
        ref={mobileNavRef}
        className={`fixed top-0 right-0 h-full w-[280px] bg-white shadow-2xl z-[200] lg:hidden transform transition-transform duration-300 ease-in-out ${
          isNavOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full p-6 overflow-y-auto">
          <div className="flex flex-col gap-4 mt-16">
            {!isLogin && (
              <div className="flex flex-col gap-4">
                <Link
                  to="/"
                  onClick={handleLinkClick}
                  className={getMobileLinkStyles("/")}
                >
                  Home
                </Link>
                <Link
                  to="/programs"
                  onClick={handleLinkClick}
                  className={getMobileLinkStyles("/programs")}
                >
                  Programs
                </Link>
                <Link
                  to="/aboutus"
                  onClick={handleLinkClick}
                  className={getMobileLinkStyles("/aboutus")}
                >
                  About us
                </Link>
                <Link
                  to="/casesfeeds"
                  onClick={handleLinkClick}
                  className={getMobileLinkStyles("/casesfeeds")}
                >
                  Projects
                </Link>
                <Link
                  to="/blogs"
                  onClick={handleLinkClick}
                  className={getMobileLinkStyles("/blogs")}
                >
                Blogs
                </Link>
              </div>
            )}

            {isLogin ? (
              <>
                <div className=""></div>
                {userInfo?.user?.role === "admin" ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={handleLinkClick}
                      className={getMobileLinkStyles("/dashboard")}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/familylisting"
                      onClick={handleLinkClick}
                      className={getMobileLinkStyles("/familylisting")}
                    >
                      Family Listing
                    </Link>
                    <Link
                      to="/memberlisting"
                      onClick={handleLinkClick}
                      className={getMobileLinkStyles("/memberlisting")}
                    >
                      Member Listing
                    </Link>
                    <Link
                      to="/propertydetailpage"
                      onClick={handleLinkClick}
                      className={getMobileLinkStyles("/propertydetailpage")}
                    >
                      Property Listing
                    </Link>
                  </>
                ) : userInfo?.user?.role === "medicalstaff" ? (
                  <Link
                    to="/dashboard/verify_fingerprint"
                    onClick={handleLinkClick}
                    className={getMobileLinkStyles("/dashboard/verify_fingerprint")}
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/familylisting"
                      onClick={handleLinkClick}
                      className={getMobileLinkStyles("/familylisting")}
                    >
                      Family Listing
                    </Link>
                    <Link
                      to="/memberlisting"
                      onClick={handleLinkClick}
                      className={getMobileLinkStyles("/memberlisting")}
                    >
                      Member Listing
                    </Link>
                    <Link
                      to="/propertydetailpage"
                      onClick={handleLinkClick}
                      className={getMobileLinkStyles("/propertydetailpage")}
                    >
                      Property Listing
                    </Link>
                  </>
                )}
                <span
                  onClick={handleLogout}
                  style={{ cursor: "pointer" }}
                  className="primary-button"
                >
                  Logout
                </span>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={handleLinkClick}
                  className={getMobileLinkStyles("/login")}
                >
                  Login
                </Link>
                <div className="mt-4">
                  <Link
                    to="/donation"
                    onClick={handleLinkClick}
                    className="primary-button inline-block text-center"
                  >
                    DONATE
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Header;
